// The record screen's error codes, and their mapping onto the shared recording-problem
// catalogue (ERRORS-1, `@scribe/core/errors`, docs/specs/errors-1.md). The COPY lives in the
// catalogue now — one place, reused by every client — so this module only says which
// catalogue entry each record-screen failure is.
import type { StreamingErrorCode } from '@scribe/core/streaming';
import {
  getRecordingProblem,
  problemCodeForStreamingError,
  type RecordingProblemCode,
} from '@scribe/core/errors';

/** Capture-stage failures the browser reports before (or instead of) anything reaching the
 * bridge — classified by `@scribe/core/errors`' `classifyCaptureError`. */
export type CaptureErrorCode =
  | 'mic_permission_denied'
  | 'mic_not_found'
  | 'mic_in_use'
  | 'capture_unsupported'
  | 'offline';

/** Every error code the record screen can surface: the bridge/wire codes, the client-only
 * `bridge_not_configured` (this build has no capture bridge — NOT a network problem, which is
 * what it was reported as until M10 dev validation 2026-08-07), and capture-stage codes.
 * Widened here rather than in `@scribe/core` because `StreamingErrorCode` is specifically the
 * close-code mapping and must never produce these. */
export type RecordErrorCode = StreamingErrorCode | 'bridge_not_configured' | CaptureErrorCode;

const CAPTURE_CODES: ReadonlySet<string> = new Set<CaptureErrorCode>([
  'mic_permission_denied',
  'mic_not_found',
  'mic_in_use',
  'capture_unsupported',
  'offline',
]);

export function isCaptureErrorCode(code: string): code is CaptureErrorCode {
  return CAPTURE_CODES.has(code);
}

/** The catalogue entry a record-screen error renders. */
export function recordErrorProblemCode(code: RecordErrorCode): RecordingProblemCode {
  return isCaptureErrorCode(code) ? code : problemCodeForStreamingError(code);
}

/** Headline copy for a RecordErrorCode (the catalogue title). */
export function streamingErrorMessage(code: RecordErrorCode): string {
  return getRecordingProblem(recordErrorProblemCode(code)).title;
}
